/*!
 * roomBeat.js — the beat grid kept with the room's, and with clocks outside
 * © 2026 Rob Canning — GPLv3
 *
 * The room keeps a timeline (server/liveRoom.js): { ms, beat, bpm }, on
 * the clock every member's grid counts on (serverClock.js). This window
 * asks for it when the room opens and when a project loads, takes every
 * livecode_timeline that comes (setTimeline), and sends its own console's
 * changes (shareTimeline): so a performer who joins late is on the beat at
 * once, and downbeat() starts a bar together. On Oscilla's server with
 * Ableton Link (server/linkBridge.js) the server answers with Link's
 * timeline instead, and passes a change on to Link.
 *
 * A livecode_timeline with a bpm and no ms or beat (OSC's /oscilla/tempo
 * with no beat) is a tempo change on the first whole beat after `at`:
 * every window counts the same grid, so they all pick the same beat.
 *
 * A timeline is three numbers, never code: what a room is allowed to carry.
 */

import { setTimeline, wholeBeatAfter, gridNow, gridBpm, nextWholeBeat, msAtBeat } from "./beatGrid.js";
import { sendSocketMessage } from "../../system/room.js";
import { projectName } from "../../system/projectContext.js";

const project = () => projectName("unknown_project");
const open = () => typeof WebSocket !== "undefined" && window.socket?.readyState === WebSocket.OPEN;
const listeners = new Set();

// Oscilla Live with no room, with Link: { send(message), anyDomain? } -- the
// clock alone on Oscilla's server (liveClockLink.js), on its clock; or the
// helper on this computer (liveHelperLink.js, clock(link)), which carries a
// grid time on any clock into its own (anyDomain). A room, when there is
// one, comes first.
let clockLink = null;
export function useClockLink(link) { clockLink = link; }

/** To the room, or else to the clock alone. True when it went. */
function sendBeat(type, payload) {
  if (open()) { sendSocketMessage(type, payload); return true; }
  if (clockLink) { clockLink.send({ type, ...payload }); return true; }
  return false;
}

/** Told of each timeline taken from the room: (timeline, message) => void. */
export function onRoomTimeline(fn) { listeners.add(fn); return () => listeners.delete(fn); }

/**
 * A livecode_timeline from the room (or Link, or OSC, through the server):
 * the grid set from it. Returns the entry set, or null.
 */
export function applyTimelineMessage(d) {
  if (!d || (d.project && d.project !== project())) return null;
  const from = d.source || "room";
  let tl = null;
  if (Number.isFinite(Number(d.ms)) && Number.isFinite(Number(d.beat))) {
    tl = setTimeline(d, from);
  } else if (Number(d.bpm) > 0) {
    // a tempo alone: from the first whole beat after `at` (the server's clock)
    const at = Number.isFinite(Number(d.at)) ? Number(d.at) : gridNow().ms + 50;
    const beat = wholeBeatAfter(at);
    tl = setTimeline({ ms: msAtBeat(beat), beat, bpm: Number(d.bpm) }, from);
  }
  if (tl) for (const fn of listeners) { try { fn(tl, d); } catch { /* a listener's own trouble */ } }
  return tl;
}

/** Ask the room for its timeline: on joining, and on each project. */
export function askRoomTimeline() {
  sendBeat("livecode_timeline_request", { project: project() });
}

/**
 * This console's change, to the room: { ms, beat, bpm } on the shared clock.
 * Only once this window counts the room's clock (domain "server"): a grid
 * time on its own clock would mean nothing to the others. True when sent.
 */
export function shareTimeline({ ms, beat, bpm }) {
  const counted = gridNow().domain === "server";
  if (!counted && !(clockLink?.anyDomain && !open())) return false;
  return sendBeat("livecode_timeline_set", { project: project(), ms, beat, bpm });
}

/**
 * downbeat(): the next whole beat becomes beat 0 -- a bar starts there --
 * here at once, and in the room. Returns the timeline.
 */
export function downbeat(leadMs = 100) {
  const ms = msAtBeat(nextWholeBeat(leadMs));
  const tl = { ms, beat: 0, bpm: gridBpm(ms) };
  setTimeline(tl, "local");
  const shared = shareTimeline(tl);
  return { ...tl, shared };
}

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("oscilla:room-timeline", (e) => applyTimelineMessage(e.detail));
  window.addEventListener("oscilla:room-open", askRoomTimeline);
  window.addEventListener("oscilla:project-loaded", askRoomTimeline);
}
